import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BookOpen, ListChecks, Pencil } from 'lucide-react';
import { Badge, EmptyState, ErrorPanel, Spinner } from '../../../school_panel/components/ui';
import { useAuthoringExam } from '../../../school_panel/api/hooks';
import { SchoolApi } from '../../../school_panel/api/client';
import AttachmentsField from '../../../school_panel/components/AttachmentsField';
import type { Attachment, AuthoringQuestion, AuthoringQuestionType } from '../../../school_panel/api/types';
import { TYPE_ICON, TYPE_LABEL } from './QuestionTypePicker';
import AnswerPreview, { withBlanks } from './AnswerPreview';

/**
 * A worksheet's or homework's questions on its detail page, each in its own type
 * (multiple choice, fill in the blank, matching, written), read only.
 */
export default function WorksheetQuestionsPreview({ examId, kind, canEdit }: { examId: number; kind: string; canEdit: boolean }) {
  const { data: exam, status, refetch } = useAuthoringExam(examId);
  const editTo = `/worksheets/${examId}/edit`;
  const editLink = canEdit && (
    <Link to={editTo} className="inline-flex min-h-[40px] items-center gap-2 rounded-[12px] border border-line-strong px-3.5 text-sm font-semibold text-ink no-underline hover:border-slate hover:text-ink">
      <Pencil size={14} aria-hidden />Edit in the worksheet editor
    </Link>
  );

  if (status === 'pending') return <div className="flex justify-center py-8"><Spinner /></div>;
  if (status === 'error' || !exam) return <ErrorPanel title="The questions could not be loaded" body="Try again in a moment." onRetry={() => refetch()} />;

  const questions = exam.questions ?? [];
  if (questions.length === 0) {
    return (
      <div className="space-y-3">
        <AttachmentsBlock attachments={exam.attachments ?? []} />
        <EmptyState compact icon={<ListChecks size={18} strokeWidth={1.8} aria-hidden />} title="No questions yet" body={`Add questions to this ${kind} in the worksheet editor.`} action={editLink || undefined} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {editLink && <div className="flex justify-end">{editLink}</div>}
      <QuestionsPreview questions={questions} attachments={exam.attachments ?? []} />
    </div>
  );
}

/** The files students open with the questions (a reading passage, a diagram), read only. */
function AttachmentsBlock({ attachments }: { attachments: Attachment[] }) {
  if (attachments.length === 0) return null;
  return (
    <section aria-label="Attachments" className="rounded-[14px] border border-line bg-surface p-4">
      <h3 className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">Attachments</h3>
      <AttachmentsField attachments={attachments} readOnly onAdd={() => undefined} onRemove={() => undefined}
        onOpen={(a) => { SchoolApi.downloadAttachment(a).catch(() => toast.error("Couldn't open the file. Try again.")); }} />
    </section>
  );
}

/** Attachments, then every question in its own type with its answer, explanation and reference. */
export function QuestionsPreview({ questions, attachments }: { questions: AuthoringQuestion[]; attachments: Attachment[] }) {
  return (
    <div className="space-y-3">
      <AttachmentsBlock attachments={attachments} />
      <ol aria-label="Questions" className="space-y-2.5">
        {questions.map((q, i) => {
          const t = q.type as AuthoringQuestionType;
          return (
            <li key={q.id} className="rounded-[14px] border border-line bg-surface p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-mono font-semibold text-muted">Q{i + 1}</span>
                <Badge tone="info"><span className="mr-1 inline-flex">{TYPE_ICON[t]}</span>{TYPE_LABEL[t] ?? q.type}</Badge>
                {q.chapter && <span className="text-muted">· {q.chapter.name}</span>}
              </div>
              <p className="mt-1.5 break-words text-sm text-ink">{withBlanks(q.text)}</p>
              <AnswerPreview q={q} />
              {q.explanation && <p className="mt-2 text-xs text-muted"><span className="font-semibold text-ink">Explanation:</span> {q.explanation}</p>}
              {q.reference && (
                <p className="mt-1 flex items-start gap-1 text-xs text-muted"><BookOpen size={12} aria-hidden className="mt-0.5 flex-none" />{q.reference}</p>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
